/**
 * Schema API — unified schema CRUD, promotion, enrichment, generation.
 */
import { apiClient, buildUrl, DEFAULT_TENANT_ID } from './apiClient';
import type {
  Schema,
  SchemaListParams,
  SchemaCreateRequest,
  SchemaUpdateRequest,
  SchemaCloneRequest,
  SchemaPromoteRequest,
  SchemaGenerateRequest,
  SchemaGenerateResponse,
  SchemaUploadRequest,
  SchemaUploadResponse,
  ConnectorSchemaExtractRequest,
  ConnectorSchemaExtractResponse,
} from './types';
export type { Schema } from './types';

// --- Unified Schema CRUD ---

/** React Query key for draft list: GET /api/v1/schemas?status=draft&limit=50 */
export const DRAFT_SCHEMAS_QUERY_KEY = 'schemas-draft' as const;

export async function listSchemas(params: SchemaListParams = {}): Promise<Schema[]> {
  const search = new URLSearchParams();
  if (params.tenantId) {
    search.set('tenant_id', params.tenantId);
  }
  if (params.status) {
    search.set('status', params.status);
  }
  if (params.name) {
    search.set('name', params.name);
  }
  if (params.projectId) {
    search.set('project_id', params.projectId);
  }
  if (params.sourceType) {
    search.set('source_type', params.sourceType);
  }
  if (typeof params.limit === 'number') {
    search.set('limit', String(params.limit));
  }
  const query = search.toString() ? `?${search.toString()}` : '';
  const response = await apiClient.get<{ schemas: Schema[] }>(
    buildUrl(`/api/v1/schemas${query}`)
  );
  return response.data?.schemas ?? [];
}

export async function createSchema(request: SchemaCreateRequest): Promise<Schema> {
  const body = {
    tenant_id: request.tenantId || DEFAULT_TENANT_ID,
    schema_name: request.schemaName,
    definition: request.definition,
    metadata: request.metadata,
    validator_config: request.validatorConfig,
    project_id: request.projectId,
    source_type: request.sourceType,
    provenance: request.provenance,
    status: request.status,
    created_by: request.createdBy,
  };
  const response = await apiClient.post<Schema>(buildUrl('/api/v1/schemas'), body);
  return response.data;
}

export async function getSchema(schemaId: string): Promise<Schema | null> {
  const response = await apiClient.get<{ schema: Schema | null }>(
    buildUrl(`/api/v1/schemas/${schemaId}`)
  );
  return response.data?.schema ?? null;
}

export async function updateSchema(schemaId: string, request: SchemaUpdateRequest): Promise<Schema> {
  const body = {
    schema_name: request.schemaName,
    status: request.status,
    definition: request.definition,
    metadata: request.metadata,
    validator_config: request.validatorConfig,
    provenance: request.provenance,
    approved_by: request.approvedBy,
    approved_at: request.approvedAt,
  };
  const response = await apiClient.patch<Schema>(
    buildUrl(`/api/v1/schemas/${schemaId}`),
    body
  );
  return response.data;
}

export interface SchemaDeleteResponse {
  /** When false, delete was rejected (e.g. dependencies). Omitted or true means success for HTTP 200 deletes. */
  success?: boolean;
  error?: string;
  schema_id?: string;
  status?: string;
  dependencies?: Record<string, any>;
  total_count?: number;
}

export async function deleteSchema(schemaId: string): Promise<SchemaDeleteResponse> {
  const response = await apiClient.delete<SchemaDeleteResponse>(
    buildUrl(`/api/v1/schemas/${schemaId}`)
  );
  return response.data;
}

export async function promoteSchema(
  schemaId: string,
  request: SchemaPromoteRequest
): Promise<Schema> {
  const body = {
    status: request.status,
    approved_by: request.approvedBy,
    metadata: request.metadata,
    schema_name: request.schemaName,
  };
  const response = await apiClient.post<Schema>(
    buildUrl(`/api/v1/schemas/${schemaId}/promote`),
    body
  );
  return response.data;
}

export async function cloneSchema(
  schemaId: string,
  request: SchemaCloneRequest
): Promise<Schema> {
  const body = {
    schema_name: request.schemaName,
    status: request.status,
    created_by: request.createdBy,
  };
  const response = await apiClient.post<Schema>(
    buildUrl(`/api/v1/schemas/${schemaId}/clone`),
    body
  );
  return response.data;
}

export async function generateSchema(
  request: SchemaGenerateRequest
): Promise<SchemaGenerateResponse> {
  const body = {
    tenant_id: request.tenantId,
    project_id: request.projectId,
    provider: request.provider,
    source_type: request.sourceType,
    payload: request.payload,
    created_by: request.createdBy,
    metadata: request.metadata,
  };
  const response = await apiClient.post<SchemaGenerateResponse>(
    buildUrl('/api/v1/schemas/generate'),
    body
  );
  return response.data;
}

export type AnnotationMode = 'structural' | 'descriptions' | 'full';

export interface AnnotateSchemaOptions {
  mode?: AnnotationMode;
  /** Operator guidance forwarded to the STRUCTURAL phase. Ignored for mode='descriptions'. */
  guidance?: string | null;
  /** Operator instructions forwarded to the DESCRIPTIONS phase. Ignored for mode='structural'. */
  instructions?: string | null;
  /** Whether descriptions phase should include template-derived validation rules. */
  includeTemplateRules?: boolean;
}

export async function annotateSchema(
  schemaId: string,
  options: AnnotateSchemaOptions = {},
): Promise<Schema> {
  const body: Record<string, any> = {
    mode: options.mode ?? 'full',
  };
  if (options.guidance) body.guidance = options.guidance;
  if (options.instructions) body.instructions = options.instructions;
  if (typeof options.includeTemplateRules === 'boolean') {
    body.include_template_rules = options.includeTemplateRules;
  }
  const response = await apiClient.post<Schema>(
    buildUrl(`/api/v1/schemas/${schemaId}/annotate`),
    body,
    { timeout: 300_000 },  // 5 min — full-mode LLM run can be 60–180s
  );
  return response.data;
}

export async function diffSchema(schemaId: string, rightSchemaId: string) {
  const params = new URLSearchParams();
  params.set('right_schema_id', rightSchemaId);
  const query = `?${params.toString()}`;
  const response = await apiClient.get(buildUrl(`/api/v1/schemas/${schemaId}/diff${query}`));
  return response.data;
}

export interface EnrichmentLogEntry {
  log_id: string;
  schema_id?: string;
  /** @deprecated Use schema_id instead */
  draft_id?: string;
  batch_id?: string;
  llm_run_id?: string;
  log_type: string;
  prompt_template?: string;
  model_id?: string;
  prompt_text?: string;
  response_text?: string;
  token_usage?: Record<string, number>;
  metadata?: Record<string, any>;
  created_at: string;
}

export async function getSchemaEnrichmentLogs(
  schemaId: string,
): Promise<{ logs: EnrichmentLogEntry[]; count: number }> {
  const response = await apiClient.get<{ schema_id: string; logs: EnrichmentLogEntry[]; count: number }>(
    buildUrl(`/api/v1/schemas/${schemaId}/enrichment-logs`),
  );
  return response.data;
}

export async function uploadSchema(payload: SchemaUploadRequest): Promise<SchemaUploadResponse> {
  const body: Record<string, any> = {
    source_type: payload.sourceType,
    content: payload.content,
    schema_name: payload.schemaName,
    schema_set_id: payload.schemaSetId,
    tenant_id: payload.tenantId || DEFAULT_TENANT_ID,
    metadata: payload.metadata || {},
    created_by: payload.createdBy || 'web-ui',
  };
  if (payload.additionalFiles && Object.keys(payload.additionalFiles).length > 0) {
    body.additional_files = payload.additionalFiles;
  }
  if (payload.selectedSheets && payload.selectedSheets.length > 0) {
    body.selected_sheets = payload.selectedSheets;
  }
  if (payload.selectedColumnsBySheet && Object.keys(payload.selectedColumnsBySheet).length > 0) {
    body.selected_columns_by_sheet = payload.selectedColumnsBySheet;
  }
  try {
    const response = await apiClient.post<SchemaUploadResponse>(
      buildUrl('/api/v1/schemas/upload'),
      body
    );
    return response.data;
  } catch (error : any) {
    if (error.response?.data) {
          const errorData = error.response.data;
          const userMessage = errorData.error || errorData.detail || errorData.message;
          if (userMessage) {
            const enhancedError = new Error(userMessage);
            (enhancedError as any).originalError = error;
            throw enhancedError;
          }
        }
        throw error;
      }
}


export async function extractSchemaFromConnector(
  payload: ConnectorSchemaExtractRequest
): Promise<ConnectorSchemaExtractResponse> {
  const body = {
    connector_id: payload.connectorId || null,
    connector_type: payload.connectorType || null,
    config: payload.config || {},
    table_names: payload.tableNames || null,
    schema_name: payload.schemaName,
    schema_set_id: payload.schemaSetId,
    tenant_id: payload.tenantId || DEFAULT_TENANT_ID,
    metadata: payload.metadata || {},
    created_by: payload.createdBy || 'web-ui',
  };
  const response = await apiClient.post<ConnectorSchemaExtractResponse>(
    buildUrl('/api/v1/schemas/extract-from-connector'),
    body
  );
  return response.data;
}

// --- Backward-compatible aliases (deprecated) ---

/**
 * Extract target table names from a schema's definition.
 * Replaces the old resolveTargetTableNames that queried schema set members.
 */
export async function resolveTargetTableNames(schema: Schema): Promise<string[]> {
  const tables = schema?.definition?.tables;
  if (tables && typeof tables === 'object') {
    return Object.keys(tables);
  }
  return [];
}

/** @deprecated Use getSchemaEnrichmentLogs instead */
export const fetchEnrichmentLogs = getSchemaEnrichmentLogs;
